import { jsx } from 'react/jsx-runtime';
import { isASTHtmlNode } from '../models/core.html.js';
import { allowedAttributeMap } from './common.html.attribute-map.js';
import { textNodeToJsxPrinter } from './common.text.js';
import { generateJsxKey } from './util.generate-jsx-key.js';

function makeHtmlNodeToJsxPrinter(options = {}, getPrinters = () => ({
  text: textNodeToJsxPrinter,
  html: htmlNodeToJsxPrinter
})) {
  let printers;
  const htmlNodeToJsxPrinter2 = (node, context) => {
    var _a;
    if (!printers) {
      printers = getPrinters();
    }
    const TagName = node.tagName;
    if (node.tagName === "br" && !context.parentNode && (options.skipLeadingBRs || options.skipTrailingBRs)) {
      const indexOfNode = context.siblingNodes.indexOf(node);
      if (options.skipLeadingBRs) {
        const firstActualContentIndex = context.siblingNodes.findIndex(
          (it) => !isASTHtmlNode(it) || it.tagName !== "br"
        );
        if (indexOfNode < firstActualContentIndex) {
          return null;
        }
      }
      if (options.skipTrailingBRs) {
        const lastActualContentIndex = context.siblingNodes.findLastIndex(
          (it) => !isASTHtmlNode(it) || it.tagName !== "br"
        );
        if (indexOfNode > lastActualContentIndex) {
          return null;
        }
      }
    }
    const attributeMap = allowedAttributeMap[node.tagName];
    if (!attributeMap) {
      return null;
    }
    const props = node.attributes ? convertHtmlAttributesToReactProps(
      node.tagName,
      node.attributes,
      attributeMap
    ) : {};
    const children = node.children !== void 0 ? printChildren(node, context, printers) : void 0;
    const externallyHandledContent = (_a = options.handleTag) == null ? void 0 : _a.call(
      options,
      node.tagName,
      {
        attributes: node.attributes,
        props,
        children
      },
      context
    );
    if (externallyHandledContent) {
      return externallyHandledContent;
    }
    return /* @__PURE__ */ jsx(TagName, { ...props, children }, generateJsxKey(context));
  };
  return htmlNodeToJsxPrinter2;
}
const htmlNodeToJsxPrinter = makeHtmlNodeToJsxPrinter();
function convertHtmlAttributesToReactProps(tagName, attributes, attributeMap) {
  const htmlProps = Object.entries(attributeMap).reduce(
    (acc, [attrName, reducer]) => {
      const value = attributes[attrName];
      if (value !== void 0) {
        reducer(acc, value);
      }
      return acc;
    },
    {}
  );
  const ariaAndDataProps = Object.entries(attributes).reduce(
    (acc, [attrName, value]) => {
      if (attrName.indexOf("data-") === 0 || attrName.indexOf("aria-") === 0) {
        acc[attrName] = value;
      }
      return acc;
    },
    {}
  );
  return {
    ...htmlProps,
    ...ariaAndDataProps,
    style: guardStyles(
      tagName,
      attributes["className"],
      convertStyleAttributeToCssProps(attributes["style"])
    )
  };
}
function convertStyleAttributeToCssProps(cssText) {
  if (!cssText) {
    return void 0;
  }
  return cssText.split(";").reduce(
    (acc, declaration) => {
      let [property, value] = declaration.split(":");
      property = property == null ? void 0 : property.trim();
      value = value == null ? void 0 : value.trim();
      if (property && value) {
        acc[fromKebabToCamelCase(property)] = value;
      }
      return acc;
    },
    {}
  );
}
function fromKebabToCamelCase(str) {
  return str.toLowerCase().replace(/^-ms-/, "ms-").replace(/[_\- ]+(\w)/g, (_, char) => char.toUpperCase());
}
function guardStyles(tagName, className, styles) {
  if (!styles) {
    return void 0;
  }
  switch (tagName.toLowerCase()) {
    case "div":
    case "span":
    case "p":
      return {
        whiteSpace: styles["whiteSpace"],
        textDecoration: styles["textDecoration"],
        textAlign: styles["textAlign"]
      };
    case "tr":
      return {
        height: styles["height"]
      };
    case "th":
    case "td":
      return {
        width: styles["width"],
        verticalAlign: styles["verticalAlign"],
        textAlign: styles["textAlign"]
      };
    case "ol":
    case "ul":
      return {
        listStyleType: (() => {
          if (styles["listStyleType"]) {
            return styles["listStyleType"];
          }
          if (styles["listStyle"]) {
            const extractedListStyleType = styles["listStyle"].match(
              /disc|armenian|circle|cjk-ideographic|decimal|decimal-leading-zero|georgian|hebrew|hiragana|hiragana-iroha|katakana|katakana-iroha|lower-alpha|lower-greek|lower-latin|lower-roman|none|square|upper-alpha|upper-greek|upper-latin|upper-roman/i
            );
            if (extractedListStyleType) {
              return extractedListStyleType[0];
            }
          }
          if (className == null ? void 0 : className.includes("list-style-upper-latin")) {
            return "upper-latin";
          } else if (className == null ? void 0 : className.includes("list-style-lower-latin")) {
            return "lower-latin";
          }
        })()
      };
  }
  return void 0;
}
function printChildren(node, context, printers) {
  var _a;
  if (!node.children) {
    return;
  }
  switch (node.tagName.toLowerCase()) {
    case "area":
    case "base":
    case "br":
    case "col":
    case "embed":
    case "hr":
    case "img":
    case "input":
    case "keygen":
    case "link":
    case "menuitem":
    case "meta":
    case "param":
    case "source":
    case "track":
    case "wbr":
      return void 0;
    case "table":
    case "thead":
    case "tbody":
    case "tfoot":
    case "tr":
      return context.printChildren(
        (_a = node.children) == null ? void 0 : _a.filter((it) => it.nodeType !== "text"),
        printers
      );
    default:
      return context.printChildren(node.children, printers);
  }
}

export { convertHtmlAttributesToReactProps, convertStyleAttributeToCssProps, guardStyles, htmlNodeToJsxPrinter, makeHtmlNodeToJsxPrinter, printChildren };
//# sourceMappingURL=common.html.js.map
